'use client'

import { Close } from '@mui/icons-material'
import { Box, IconButton, Typography } from '@mui/material'
import { alpha, styled } from '@mui/material/styles'
import type { ReactNode } from 'react'

type ComposerContextTagProps = {
  /** Small leading glyph (entity / product icon). */
  icon?: ReactNode
  /** Context label shown like Linear's tagged project name. */
  label: string
  /** Optional trailing controls (e.g. new-chat) rendered before the dismiss control. */
  endAdornment?: ReactNode
  /** When set, shows a quiet dismiss control on the right. */
  onDismiss?: () => void
  dismissLabel?: string
  dismissDataCy?: string
  /** `header` is the panel identity strip; `inline` nests under the composer input. */
  variant?: 'header' | 'inline'
  'data-cy'?: string
}

/**
 * Linear-style context tag: a quiet strip with icon + name (+ optional dismiss), used as the
 * composer header identity or as a pinned context above the input — not a MUI Chip.
 */
export default function ComposerContextTag({
  icon,
  label,
  endAdornment,
  onDismiss,
  dismissLabel,
  dismissDataCy,
  variant = 'header',
  'data-cy': dataCy
}: ComposerContextTagProps) {
  return (
    <TagBar data-cy={dataCy} $variant={variant}>
      <TagIdentity>
        {icon ? <TagIcon aria-hidden>{icon}</TagIcon> : null}
        <TagLabel title={label}>{label}</TagLabel>
      </TagIdentity>
      {(endAdornment || onDismiss) && (
        <TagActions>
          {endAdornment}
          {onDismiss ? (
            <IconButton
              size="small"
              onClick={onDismiss}
              aria-label={dismissLabel ?? 'Dismiss'}
              edge="end"
              data-cy={dismissDataCy}
            >
              <Close fontSize="small" />
            </IconButton>
          ) : null}
        </TagActions>
      )}
    </TagBar>
  )
}

const TagBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$variant'
})<{ $variant: 'header' | 'inline' }>(({ theme, $variant }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  minHeight: $variant === 'header' ? 40 : 34,
  padding: theme.spacing($variant === 'header' ? 0.75 : 0.5, 1, $variant === 'header' ? 0.75 : 0.5, 1.5),
  flexShrink: 0,
  backgroundColor:
    $variant === 'header' ?
      theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04)
      : alpha(theme.palette.common.black, 0.03)
    : 'transparent',
  borderBottom: $variant === 'header' ? `1px solid ${theme.palette.divider}` : undefined
}))

const TagIdentity = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
  flex: 1
}))

const TagIcon = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  flexShrink: 0,
  '& .MuiSvgIcon-root': {
    fontSize: 18
  }
}))

const TagLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  fontWeight: 500,
  letterSpacing: 0.01,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}))

const TagActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.25),
  flexShrink: 0,
  color: theme.palette.text.secondary
}))
